import React, { useEffect, useRef, useState, useLayoutEffect } from 'react';
import PropTypes from 'prop-types';
import { SvgIcon } from 'common/react/components/SvgIcon';

/**
 * ScrollableFilterBar - Handles horizontal scrolling with arrow controls
 *
 * @param {object} props - Props.
 * @param {React.Node} props.children - The content to render inside the scrollable area.
 * @param {React.Node} props.settingsButton - Optional settings button to display.
 * @param {React.Node} props.roundButtons - Optional round buttons to display.
 * @returns {React.Element} Component.
 */
export const ScrollableFilterBar = ({
    children,
    settingsButton,
    roundButtons
}) => {
    const [isLeftArrowVisible, setIsLeftArrowVisible] = useState(false);
    const [isRightArrowVisible, setIsRightArrowVisible] = useState(false);

    const filterBarRef = useRef(null);
    const leftArrowRef = useRef(null);
    const rightArrowRef = useRef(null);

    const updateArrows = () => {
        const filterBar = filterBarRef.current;
        const leftArrow = leftArrowRef.current;
        const rightArrow = rightArrowRef.current;

        if (!filterBar || !leftArrow || !rightArrow) {
            return;
        }

        const scrollLeft = filterBar.scrollLeft;
        const maxScrollLeft = filterBar.scrollWidth - filterBar.clientWidth;
        const leftVisible = scrollLeft > 0;
        const rightVisible = maxScrollLeft > 0 && scrollLeft < maxScrollLeft;

        leftArrow.style.display = leftVisible ? 'block' : 'none';
        rightArrow.style.display = rightVisible ? 'block' : 'none';
        setIsLeftArrowVisible(leftVisible);
        setIsRightArrowVisible(rightVisible);
    };

    useLayoutEffect(() => {
        updateArrows();
    }, [children]);

    // Update the scroll bar arrows when the filterBar content changes
    useEffect(() => {
        const filterBar = filterBarRef.current;
        if (!filterBar) {
            return () => null;
        }

        const observer = new ResizeObserver(() => {
            updateArrows();
        });

        observer.observe(filterBar);
        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        const filterBar = filterBarRef.current;
        const leftArrow = leftArrowRef.current;
        const rightArrow = rightArrowRef.current;

        if (!filterBar || !leftArrow || !rightArrow) {
            return () => null;
        }

        const scrollRight = () => {
            filterBar.scrollBy({ left: 200, behavior: 'smooth' });
        };

        const scrollLeft = () => {
            filterBar.scrollBy({ left: -200, behavior: 'smooth' });
        };

        filterBar.addEventListener('scroll', updateArrows);
        window.addEventListener('resize', updateArrows);
        rightArrow.addEventListener('click', scrollRight);
        leftArrow.addEventListener('click', scrollLeft);

        return () => {
            rightArrow.removeEventListener('click', scrollRight);
            leftArrow.removeEventListener('click', scrollLeft);
            filterBar.removeEventListener('scroll', updateArrows);
            window.removeEventListener('resize', updateArrows);
        };
    }, [children]);

    return (
        <>
            <div className="filter-bar-wrapper">
                <button
                    className="scroll-arrow left"
                    aria-label="Scroll left"
                    ref={leftArrowRef}
                    tabIndex={0}
                >
                    <SvgIcon className="icon" icon="chevron-down" />
                </button>

                {isLeftArrowVisible && <div className="fade-overlay left" />}

                <div className="filter-bar" ref={filterBarRef}>
                    {children}
                </div>

                {isRightArrowVisible && <div className="fade-overlay right" />}

                <button
                    className="scroll-arrow right"
                    aria-label="Scroll right"
                    ref={rightArrowRef}
                    tabIndex={0}
                >
                    <SvgIcon className="icon" icon="chevron-down" />
                </button>

                {!!settingsButton && (
                    <div className="stats-leaders-nav__filter-button-wrapper">
                        {settingsButton}
                    </div>
                )}
            </div>
            {!!roundButtons && (
                <div className="stats-leaders-nav__round-buttons-wrapper">
                    {roundButtons}
                </div>
            )}
        </>
    );
};

ScrollableFilterBar.propTypes = {
    children: PropTypes.node.isRequired,
    settingsButton: PropTypes.node,
    roundButtons: PropTypes.node
};
